import { createContext, useContext, useState, ReactNode } from 'react';

export interface OnboardingResponses {
  // Step 3: Who's in your life
  lifeCompanions: string[];

  // Step 4: Housing
  housingType: 'own' | 'rent' | 'parents' | null;
  hasKPR: boolean | null;

  // Step 5: Loans
  loans: string[];

  // Step 6: Transportation
  transportation: string[];

  // Step 7: Monthly expenses
  monthlyExpenses: string[];

  // Step 8: Subscriptions
  subscriptions: string[];

  // Step 10: Big but infrequent expenses
  infrequentExpenses: string[];

  // Step 11: Goals/Savings priorities
  savingsPriorities: string[];

  // Step 12: Fun expenses
  funExpenses: string[];
}

interface OnboardingContextType {
  responses: OnboardingResponses;
  currentStep: number;
  highestStepReached: number;
  budgetGenerated: boolean;
  updateResponses: (key: keyof OnboardingResponses, value: any) => void;
  setCurrentStep: (step: number) => void;
  markStepCompleted: (step: number) => void;
  setBudgetGenerated: (generated: boolean) => void;
  canAccessStep: (step: number) => boolean;
  resetOnboarding: () => void;
}

const initialResponses: OnboardingResponses = {
  lifeCompanions: [],
  housingType: null,
  hasKPR: null,
  loans: [],
  transportation: [],
  monthlyExpenses: [],
  subscriptions: [],
  infrequentExpenses: [],
  savingsPriorities: [],
  funExpenses: [],
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [responses, setResponses] = useState<OnboardingResponses>(initialResponses);
  const [currentStep, setCurrentStep] = useState(1);
  const [highestStepReached, setHighestStepReached] = useState(1);
  const [budgetGenerated, setBudgetGenerated] = useState(false);

  const updateResponses = (key: keyof OnboardingResponses, value: any) => {
    setResponses(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const markStepCompleted = (step: number) => {
    setHighestStepReached(prev => Math.max(prev, step + 1));
    setCurrentStep(step + 1);
  };

  const canAccessStep = (step: number) => {
    // Steps 1-2 are always accessible (welcome and intro)
    if (step <= 2) return true;

    // Step 14 requires budget to be generated (step 13 completed)
    if (step === 14) return budgetGenerated;

    // Other steps require previous step to be completed
    return step <= highestStepReached;
  };

  const resetOnboarding = () => {
    setResponses(initialResponses);
    setCurrentStep(1);
    setHighestStepReached(1);
    setBudgetGenerated(false);
  };

  return (
    <OnboardingContext.Provider
      value={{
        responses,
        currentStep,
        highestStepReached,
        budgetGenerated,
        updateResponses,
        setCurrentStep,
        markStepCompleted,
        setBudgetGenerated,
        canAccessStep,
        resetOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
